import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { WarningCircle, MagnifyingGlass, CaretRight, ClipboardText } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB");
};

export default function OverdueChecksPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/checks/overdue").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  const visible = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return `${r.asset_number} ${r.asset_name}`.toLowerCase().includes(s);
  });

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="overdue-checks-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
          <WarningCircle size={24} weight="fill" className="text-red-600" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Overdue Checks</h1>
          <p className="font-body text-sm text-slate-500">Assets whose periodic check is overdue or has never been recorded.</p>
        </div>
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="overdue-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset number or name…" className="pl-9 h-11" />
      </div>

      {loading ? (
        <p className="font-body text-slate-400 py-10 text-center">Loading…</p>
      ) : visible.length === 0 ? (
        <div data-testid="overdue-empty" className="text-center py-16 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50">
          <ClipboardText size={40} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
          <p className="font-body font-semibold text-emerald-700">{rows.length === 0 ? "Nothing overdue — all checks are up to date." : "No assets match your search."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="overdue-list">
          {visible.map((r) => (
            <button key={r.asset_id} data-testid={`overdue-row-${r.asset_id}`} onClick={() => nav(`/assets/${r.asset_id}`)}
              className="w-full text-left bg-white border-2 border-slate-100 hover:border-red-300 rounded-xl p-4 flex items-center gap-4 transition-colors duration-150">
              <div className="flex-1 min-w-0">
                <p className="font-body font-bold text-slate-900 truncate">{r.asset_number} · {r.asset_name}</p>
                <p className="font-body text-xs text-slate-500 mt-0.5">
                  Last check: <span className="font-semibold text-slate-700">{fmtDate(r.last_check)}</span>
                  {" · "}Every {r.frequency_days} day{r.frequency_days === 1 ? "" : "s"}
                  {r.checks?.length ? ` · ${r.checks.join(", ")}` : ""}
                </p>
              </div>
              <span className={`shrink-0 text-[11px] font-black uppercase tracking-wide rounded-full px-2.5 py-1 ${r.never_checked ? "bg-slate-800 text-white" : "bg-red-100 text-red-700"}`}>
                {r.never_checked ? "Never checked" : `${r.days_overdue}d overdue`}
              </span>
              <CaretRight size={16} weight="bold" className="text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
